import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Query, Predicate } from '@syncfusion/ej2-data';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DropDown } from 'src/app/interfaces/drop-down.interface';
import { SecurityPolicies } from 'src/app/services/auth-variables';
import { AuthService } from 'src/app/services/auth.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { TranslateService } from '@ngx-translate/core';

export interface ILocationChangedToModel {
  location: DropDown<number>;
  organizationId: number;
  orgChanged: boolean;
}

@Component({
  selector: 'app-move-to-organization-form',
  templateUrl: './move-to-organization-form.component.html',
  styleUrls: ['./move-to-organization-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class MoveToOrganizationFormComponent {
  @Output() destinationOrgChanged = new EventEmitter<DropDown<number>>();

  @Input() set formVM(value: {
    billableOrgId: number;
    orgId: number;
    billableOrgName?: string;
  }) {
    if (value === null || value === undefined || value.billableOrgId === null || value.orgId === null) return;
    this._initFormVM = value;
    this.initializeForm();
  }

  @Input() showDestinationBillableOrg: boolean = true;
  @Input() showEnableBillableOrgEdit: boolean = true;

  _initFormVM: any = {
    billableOrgId: null,
    orgId: null,
    billableOrgName: '',
    currentOrgIsAffiliationSource: false,
  };

  /**
   * Translated "Source Org" when the current organization is an affiliation source, otherwise null
   * so the suffix renders nothing. Move Users does not set the flag, so it never appears there.
   */
  get currentOrgSourceLabel(): string | null {
    return this._initFormVM?.currentOrgIsAffiliationSource
      ? this.translate.instant('MoveForms.LocationMove.SourceOrgLabel')
      : null;
  }

  formGroup: FormGroup = this.formBuilder.group({
    isEditEnabled: [false],
    billableOrg: [{ value: null, disabled: true }],
    org: [null],
  });

  formControlCols: number = 9;
  securityPolicies = SecurityPolicies;
  billableOrgList: DropDown<number>[] = null;
  orgList: DropDown<number>[] = null;

  get isEditEnabledDisabled(): Observable<boolean> {
    return this.authService.passesPolicyOf(
      this.securityPolicies.RequireSuperUser,
    );
  }

  get currentBillableOrgId() {
    return this._initFormVM?.billableOrgId;
  }

  get currentOrgId() {
    return this._initFormVM?.orgId;
  }

  get currentLocationId() {
    return this._initFormVM?.locationId;
  }

  get billableOrg() {
    return this.billableOrgFormControl?.value;
  }
  set billableOrg(value: any) {
    this.billableOrgFormControl?.setValue(value);
  }

  get org() {
    return this.orgFormControl?.value;
  }
  set org(value: any) {
    this.orgFormControl?.setValue(value);
  }

  get billableOrgFormControl() {
    return this.formGroup.get('billableOrg');
  }
  get orgFormControl() {
    return this.formGroup.get('org');
  }

  constructor(
    private formBuilder: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private organizationService: OrganizationService,
    private authService: AuthService,
    private translate: TranslateService,
  ) { }

  /*
   * Event Handlers
   */

  onBillableOrgValueChanged(selectedItem: DropDown<number>) {
    if (selectedItem === null || selectedItem === undefined) return;
    this._initFormVM.selectedBillableOrgId = selectedItem.value;

    this.resetOrgs();
    this.loadOrgDropdown(selectedItem.value);
    if (selectedItem.value === this.currentBillableOrgId) {
      this.org = this.currentOrgId;
      this.orgFormControl.enable();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.orgFormControl.enable();
    this.changeDetectorRef.markForCheck();
  }

  onDestinationOrgValueChanged(selectedItem: DropDown<number>) {
    if (selectedItem === null || selectedItem === undefined) return;
    this._initFormVM.destinationOrgId = selectedItem.value;
    this.destinationOrgChanged.emit(selectedItem);
    this.changeDetectorRef.markForCheck();
  }

  onEnableEditValueChanged($event: MatCheckboxChange) {
    const isEditEnabled: boolean = $event.checked;

    this.resetOrgs();

    if (isEditEnabled) {
      this.billableOrgFormControl.enable();
      this.orgFormControl.disable();
    } else {
      setTimeout(() => {
        //reset to the initial values
        this.resetBillableOrg();
        this.org = this.currentOrgId;
        this.loadOrgDropdown(this.currentBillableOrgId);
        this.billableOrgFormControl.disable();
        this.orgFormControl.enable();
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  /*
   * Private Methods
   */
  private resetBillableOrg() {
    //reset billable org dropdown value to the initial value
    this.billableOrgFormControl.setValue(this._initFormVM.billableOrgId);
  }

  private initializeForm() {
    this.billableOrg = this.currentBillableOrgId;
    this.org = this.currentOrgId;

    this.loadBillableOrgDropDown();
    this.loadOrgDropdown(this.currentBillableOrgId);
  }

  private resetOrgs() {
    this.org = null;
    this.orgList = [];
  }



  /**
   * Data Load Methods
   */
  private loadBillableOrgDropDown() {
    if (this.billableOrgList?.length ?? 0 > 1) return;

    this.billableOrg = this.currentBillableOrgId;
    this.organizationService
      .getBillableOrganizationHierarchy()
      .pipe(
        map((data) => {
          return data.map((x) => {
            x.value = parseInt(x.value);
            return x;
          });
        }),
      )
      .subscribe({
        next: (data) => {
          this.billableOrgList = this.getBillableOrgListWithFallback(data);
          this.billableOrg = this.currentBillableOrgId;
          this.changeDetectorRef.markForCheck();
        },
        complete: this.getDataCompleted,
      });
  }

  private getBillableOrgListWithFallback(data: DropDown<number>[]): DropDown<number>[] {
    const safeData = data ?? [];
    const currentBillableOrgId = this.currentBillableOrgId;
    if (!currentBillableOrgId) {
      return safeData;
    }

    const hasCurrentOption = safeData.some(x => Number(x.value) === Number(currentBillableOrgId));
    if (hasCurrentOption) {
      return safeData;
    }

    const fallbackName = this._initFormVM?.billableOrgName || '';
    const fallbackOption: DropDown<number> = {
      value: currentBillableOrgId,
      text: fallbackName
        ? `${fallbackName} - ${currentBillableOrgId}`
        : `${currentBillableOrgId}`,
    };

    return [fallbackOption, ...safeData];
  }

  private loadOrgDropdown(billableOrgId: number) {
    this.orgList = []; //remove all items
    this.organizationService
      .getMoveLocationOrganizationDropDown(billableOrgId)
      .subscribe({
        next: (data) => {
          this.orgList = data;
          this.changeDetectorRef.markForCheck();
        },
        complete: () => {
          this.getDataCompleted();
        },
      });
  }

  private getDataCompleted = () => {
    this.changeDetectorRef.markForCheck();
  };

  onBillableOrgDropDownFilter(e: any) {
    this.updateDropdownFilter(e, this.billableOrgList);
  }

  onDestinationOrgDropDownFilter(e: any) {
    this.updateDropdownFilter(e, this.orgList);
  }

  updateDropdownFilter(e: any, data: DropDown<number>[]): void {
    // frame the query based on search string with filter type.
    let namePredicate: Predicate = new Predicate('text', 'contains', e.text, true);
    let idPredicate: Predicate = new Predicate('value', 'contains', e.text);

    let combinedPredicate: Predicate = idPredicate.or(namePredicate);
    let query = new Query().where(combinedPredicate);

    // pass the filter data source, filter query to updateData method.
    e.updateData(data, query)
  }
}

